import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Loader2, Send, Mail } from "lucide-react";
import { GroupedChanges, ChangeEntry } from "@/hooks/useEmployeeChanges";

interface NotificationChangesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  changes: ChangeEntry[];
  grouped: GroupedChanges;
  onSend: (selectedChanges: GroupedChanges) => Promise<void>;
  isSending: boolean;
}

// Icons für Kategorien
const categoryIcons: Record<string, string> = {
  "Stammdaten": "👤",
  "Gehalt": "💰",
  "Bonus": "🎯",
  "Benefits": "🎁",
  "Bewertung": "📊",
  "Arbeitskleidung": "👔",
  "Werkzeug": "🔧",
  "Annehmlichkeiten": "❤️",
  "KFZ-Kosten": "🚗",
  "Dokumente": "📄",
  "Stunden/Abwesenheit": "⏰",
  "Mitarbeitergespräch": "💬",
  "Zugangsdaten": "🔐",
  "Mobiltelefon": "📱",
  "Stellenbeschreibung": "📝",
};

export function NotificationChangesDialog({
  open,
  onOpenChange,
  changes,
  grouped,
  onSend,
  isSending,
}: NotificationChangesDialogProps) {
  const [selectedCategories, setSelectedCategories] = useState<Set<string>>(new Set());

  // Beim Öffnen alle Kategorien auswählen
  useEffect(() => {
    if (open) {
      setSelectedCategories(new Set(Object.keys(grouped)));
    }
  }, [open, grouped]);

  const handleToggleCategory = (category: string) => {
    const newSelected = new Set(selectedCategories);
    if (newSelected.has(category)) {
      newSelected.delete(category);
    } else {
      newSelected.add(category);
    }
    setSelectedCategories(newSelected);
  };

  const handleSelectAll = () => {
    setSelectedCategories(new Set(Object.keys(grouped)));
  };

  const handleDeselectAll = () => {
    setSelectedCategories(new Set());
  };

  const handleSend = async () => {
    // Nur ausgewählte Kategorien übermitteln
    const filteredChanges: GroupedChanges = {};
    for (const category of selectedCategories) {
      if (grouped[category]) {
        filteredChanges[category] = grouped[category];
      }
    }
    await onSend(filteredChanges);
  };

  const allSelected = selectedCategories.size === Object.keys(grouped).length;
  const noneSelected = selectedCategories.size === 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5 text-primary" />
            Änderungen mitteilen
          </DialogTitle>
          <DialogDescription>
            Wähle aus, welche Änderungen dem Mitarbeiter per E-Mail mitgeteilt werden sollen.
          </DialogDescription>
        </DialogHeader>

        <div className="flex justify-between items-center mb-2">
          <span className="text-sm text-muted-foreground">
            {selectedCategories.size} von {Object.keys(grouped).length} Bereichen ausgewählt
          </span>
          <div className="flex gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleSelectAll}
              disabled={allSelected}
            >
              Alle
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleDeselectAll}
              disabled={noneSelected}
            >
              Keine
            </Button>
          </div>
        </div>

        <ScrollArea className="max-h-[400px] pr-4">
          <div className="space-y-3">
            {Object.entries(grouped).map(([category, data]) => {
              const isSelected = selectedCategories.has(category);
              const icon = categoryIcons[category] || "📌";
              
              return (
                <div
                  key={category}
                  className={`p-3 rounded-lg border transition-colors cursor-pointer ${
                    isSelected 
                      ? "border-primary/50 bg-primary/5" 
                      : "border-border bg-muted/30 opacity-60"
                  }`}
                  onClick={() => handleToggleCategory(category)}
                >
                  <div className="flex items-start gap-3">
                    <Checkbox
                      id={`cat-${category}`}
                      checked={isSelected}
                      onCheckedChange={() => handleToggleCategory(category)}
                      className="mt-0.5"
                    />
                    <div className="flex-1 min-w-0">
                      <Label 
                        htmlFor={`cat-${category}`}
                        className="flex items-center gap-2 font-medium cursor-pointer"
                      >
                        <span className="text-lg">{icon}</span>
                        <span>{category}</span>
                      </Label>
                      
                      <div className="mt-1.5 flex flex-wrap gap-1">
                        {data.actions.map((action) => (
                          <Badge 
                            key={action} 
                            variant="secondary" 
                            className="text-xs"
                          >
                            {action}
                          </Badge>
                        ))}
                      </div>
                      
                      {data.fields.length > 0 && (
                        <div className="mt-2 text-sm text-muted-foreground">
                          <span className="font-medium">Felder: </span>
                          {data.fields.slice(0, 5).join(", ")}
                          {data.fields.length > 5 && (
                            <span className="text-muted-foreground/70">
                              {" "}+{data.fields.length - 5} weitere
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </ScrollArea>

        <DialogFooter className="mt-4">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSending}
          >
            Abbrechen
          </Button>
          <Button
            onClick={handleSend}
            disabled={isSending || noneSelected}
          >
            {isSending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Wird gesendet...
              </>
            ) : (
              <>
                <Send className="mr-2 h-4 w-4" />
                {selectedCategories.size} {selectedCategories.size === 1 ? "Bereich" : "Bereiche"} senden
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
